import React from "react";
import { Box } from "@mui/material";
import RoomServiceRoundedIcon from "@mui/icons-material/RoomServiceRounded";
import { resizedImage } from "../../utils/resizedImage";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY } from "../../utils/designTokens";
import { ROW_SX, SectionHead, useRowScroller } from "./listUi";

const toTitleCase = (str) =>
  String(str || "")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * What the row offers, from the restaurants on screen: cuisines that have a
 * picture (getCuisines), then menu categories (their generated icons).
 * Catering categories are reached through the Catering tile instead.
 */
export function cuisineChoices(restaurants, cuisineData) {
  const activeNames = new Set(restaurants.flatMap((r) => r.cuisines ?? []));
  const withImages = (cuisineData ?? []).filter((c) => activeNames.has(c.name));
  const cuisines = (withImages.length ? withImages : [...activeNames].filter(Boolean).map((name) => ({ name, image: null })))
    .map((c) => ({ key: `cuisine:${c.name}`, kind: "cuisine", title: c.name, icon: c.image || null }));

  const cateringTitles = new Set(
    restaurants.flatMap((r) => r.categories ?? []).filter((c) => c.isCatering && c.title).map((c) => c.title.trim().toLowerCase())
  );
  const legacy = restaurants.flatMap((r) => r.categories ?? []).filter((c) => !c.isCatering);
  const iconByTitle = new Map();
  legacy.forEach((c) => {
    if (c.title && c.icon) iconByTitle.set(c.title.trim().toLowerCase(), c.icon);
  });
  // A section that only exists on items has no category of its own; the
  // server sends the icon shared under its name (categoryIcon).
  const advanced = restaurants.flatMap((r) =>
    (r.advancedMenus ?? []).flatMap((m) =>
      (m.items ?? [])
        .filter((it) => it.categoryName && !cateringTitles.has(it.categoryName.trim().toLowerCase()))
        .map((it) => ({
          title: it.categoryName,
          icon: iconByTitle.get(it.categoryName.trim().toLowerCase()) || it.categoryIcon || null,
        }))
    )
  );
  const seen = new Set(cuisines.map((c) => c.title.trim().toLowerCase()));
  const categories = [...legacy, ...advanced]
    .filter((c) => c.title && !seen.has(c.title.trim().toLowerCase()) && seen.add(c.title.trim().toLowerCase()))
    .map((c) => ({ key: `category:${c.title}`, kind: "category", title: toTitleCase(c.title), raw: c.title, icon: c.icon || null }));
  return [...cuisines, ...categories];
}

/** Does restaurant `r` match the chosen cuisine or category? */
export function matchesChoice(r, choice) {
  if (!choice) return true;
  if (choice.kind === "cuisine") return (r.cuisines ?? []).includes(choice.title);
  const want = String(choice.raw || choice.title).trim().toLowerCase();
  const same = (t) => String(t || "").trim().toLowerCase() === want;
  return (
    (r.categories ?? []).some((c) => same(c.title)) ||
    (r.advancedMenus ?? []).some((m) => (m.items ?? []).some((it) => same(it.categoryName)))
  );
}

function Tile({ label, selected, pressable = true, onClick, children, ringless }) {
  return (
    <Box component="li" sx={{ flex: "0 0 auto", scrollSnapAlign: "start" }}>
      <Box
        component="button"
        type="button"
        data-row-item
        onClick={onClick}
        aria-pressed={pressable ? selected : undefined}
        sx={{
          ...BUTTON_RESET,
          ...FOCUS_RING,
          width: 76,
          py: 0.75,
          borderRadius: "14px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.75,
          bgcolor: selected ? C.redTint : "transparent",
          transition: "background-color .15s ease",
          "&:hover": { bgcolor: selected ? C.redTint : C.surface },
        }}
      >
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            overflow: "hidden",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            // Selected: a 3px red ring, set off by a thin gap so it shows round the solid red "All" too.
            boxShadow: ringless ? "none" : selected ? `0 0 0 2px ${C.redTint}, 0 0 0 5px ${C.red}` : `inset 0 0 0 1px ${C.border}`,
            bgcolor: C.surface,
          }}
        >
          {children}
        </Box>
        <Box
          component="span"
          sx={{
            maxWidth: "100%",
            px: 0.5,
            boxSizing: "border-box",
            fontFamily: FONT_BODY,
            fontSize: 12,
            fontWeight: selected ? 700 : 500,
            lineHeight: 1.25,
            color: C.ink,
            textAlign: "center",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {label}
        </Box>
      </Box>
    </Box>
  );
}

/**
 * "What are you in the mood for?": Catering (when nearby restaurants do it),
 * All, then each cuisine and category. `selectedKey` null means All.
 */
function CuisineRow({ choices, selectedKey, onSelect, showCatering, onCatering, title = "What are you in the mood for?", id = "mood-title" }) {
  const row = useRowScroller();
  return (
    <Box component="section" aria-labelledby={id}>
      <SectionHead id={id} title={title} row={row} rowLabel="types" />
      <Box component="ul" ref={row.ref} onKeyDown={row.onKeyDown} aria-labelledby={id} sx={{ ...ROW_SX, gap: 0.25 }}>
        {showCatering && (
          <Tile label="Catering" pressable={false} onClick={onCatering} ringless>
            <Box sx={{ width: "100%", height: "100%", bgcolor: C.redTint, display: "flex", alignItems: "center", justifyContent: "center", color: C.red }}>
              <RoomServiceRoundedIcon aria-hidden="true" sx={{ fontSize: 24 }} />
            </Box>
          </Tile>
        )}
        <Tile label="All" selected={!selectedKey} onClick={() => onSelect(null)} ringless={!selectedKey ? false : true}>
          <Box aria-hidden="true" sx={{ width: "100%", height: "100%", bgcolor: C.red, color: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700 }}>
            All
          </Box>
        </Tile>
        {choices.map((c) => (
          <Tile key={c.key} label={c.title} selected={selectedKey === c.key} onClick={() => onSelect(selectedKey === c.key ? null : c.key)}>
            {c.icon ? (
              <Box component="img" src={resizedImage(c.icon, 128)} alt="" loading="lazy" decoding="async" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <Box component="span" aria-hidden="true" sx={{ fontFamily: FONT_BODY, fontSize: 17, fontWeight: 700, color: C.muted }}>
                {c.title.charAt(0).toUpperCase()}
              </Box>
            )}
          </Tile>
        ))}
      </Box>
    </Box>
  );
}

export default React.memo(CuisineRow);
